import { useEffect, useCallback, useMemo } from "react";
import MainLayout from "../../../layouts/MainLayout";
import { usePermissions } from "../../../hooks/usePermissions";
import { useNavigate, useLocation } from "react-router-dom";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import ConfirmationModal from "../../../components/modals/ConfirmationModal";
import useLoader from "../../../hooks/useLoader";
import useNotification from "../../../hooks/useNotification";
import { maskCpf, maskCnpj } from "../../../utils/maskUtils";
import useBaseService from "../../../hooks/services/useBaseService";
import { entities } from "../../../constants/entities";
import PageHeader from "../../../components/PageHeader";
import useAction from "../../../hooks/useAction";
import useUrlFilters from "../../../hooks/useUrlFilters";
import useGenericFilter from "../../../hooks/filters/useGenericFilter";
import { transformFiltersForUrl } from "../../../utils/transformFiltersForUrl";
import AutoCompleteFilter from "../../../components/AutoCompleteFilter";
import baseService from "../../../services/baseService";
import TableWithInactiveFilter from "../../../components/table/TableWithInactiveFilter";
import FilterCollapse from "../../../components/FilterCollapse";
import useQueryWithLoader from "../../../hooks/useQueryWithLoader";

const SuppliersPage = () => {
  const { canAccess } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const { get: baseServiceGet } = useBaseService(navigate);

  // Padronização de filtros via useUrlFilters
  const {
    filters,
    setFilters: applyUrlFilters,
    pagination,
    changePage,
    setPagination,
  } = useUrlFilters(
    {
      alias: [],
      name: [],
      cpf_cnpj: [],
      deleted_at: false,
    },
    "equipment_suppliers",
  );

  // --- Lógica de busca de fornecedores com TanStack Query ---
  const {
    data: suppliersData,
    isLoading: isLoadingSuppliers,
    isFetching: isFetchingSuppliers,
    isError: isErrorSuppliers,
    error: suppliersError,
  } = useQueryWithLoader({
    queryKey: ["equipment_suppliers", filters, pagination],
    queryFn: async () => {
      const filtersTranformed = transformFiltersForUrl(filters);
      const params = {
        filters: filtersTranformed,
        page: pagination.page,
        perPage: pagination.perPage,
        sort: { column: "id", order: "desc" },
      };
      const response = await baseServiceGet(entities.suppliers.get, params);
      return response.result;
    },
    staleTime: 60 * 1000, // Cacheia os dados por 60 segundos
    select: (data) => {
      return {
        suppliers: data.data.map((supplier) => {
          const numericValue = supplier.cpf_cnpj
            ? supplier.cpf_cnpj.replace(/\D/g, "")
            : "";
          return {
            id: supplier.id,
            alias: supplier.alias,
            name: supplier.name,
            cpf_cnpj:
              numericValue.length <= 11
                ? maskCpf(numericValue)
                : maskCnpj(numericValue),
            isDeleted: !!supplier.deleted_at, // Booleano para controle de ações
          };
        }),
        totalPages: data.last_page,
      };
    },
  });

  const { suppliers = [], totalPages = 1 } = suppliersData || {};

  const columns = [
    { key: "alias", label: "Apelido" },
    { key: "name", label: "Nome" },
    { key: "cpf_cnpj", label: "CPF/CNPJ" },
  ];

  // --- Gerenciamento de Loader com base nos estados do TanStack Query ---
  useEffect(() => {
    if (isLoadingSuppliers) {
      showLoader();
    } else {
      hideLoader();
    }
  }, [isLoadingSuppliers, showLoader, hideLoader]);

  // --- Notificação de Erro para a Query de Fornecedores ---
  useEffect(() => {
    if (isErrorSuppliers) {
      console.error(
        "SuppliersPage: Erro ao carregar fornecedores (useQuery):",
        suppliersError,
      );
      showNotification(
        "error",
        `Erro ao carregar fornecedores: ${
          suppliersError.response?.data?.message || suppliersError.message
        }`,
      );
    }
  }, [isErrorSuppliers, suppliersError, showNotification]);

  const {
    openModalConfirmation,
    action,
    handleConfirmAction,
    handleCancelConfirmation,
    selectedItem,
  } = useAction(navigate);

  // Lógica de Aplicação e Limpeza de Filtros (via useGenericFilter)
  const handleFilterApply = useCallback(
    (currentLocalFilterValues) => {
      const transformed = transformFiltersForUrl(currentLocalFilterValues);
      applyUrlFilters(transformed);
    },
    [applyUrlFilters],
  );

  const handleFilterClear = useCallback(
    (initialLocalFilterState) => {
      const transformedInitial = transformFiltersForUrl(
        initialLocalFilterState,
      );
      applyUrlFilters(transformedInitial);
    },
    [applyUrlFilters],
  );

  // Inicializa o useGenericFilter com o estado inicial dos filtros do formulário
  const { localFilterValues, handleFilterChange, handleSubmit, handleClear } =
    useGenericFilter(
      {
        alias: [],
        name: [],
        cpf_cnpj: [],
        deleted_at: false,
      },
      handleFilterApply,
      handleFilterClear,
    );

  // Inputs para o formulário de filtros
  const inputsfilters = [
    {
      label: "Fornecedor",
      component: (
        <AutoCompleteFilter
          service={baseService}
          columnDataBase={["alias", "name", "cpf_cnpj"]}
          model="supplier"
          value={localFilterValues.alias}
          onChange={(selected) => {
            const val = selected?.value;
            handleFilterChange("id", val ? { $eq: val } : null);
          }}
          placeholder="Filtre os fornecedores pelo nome, apelido ou CPF/CNPJ"
        />
      ),
    },
  ];

  // useEffect para mensagens de estado de localização (mantido)
  useEffect(() => {
    if (location.state?.message) {
      showNotification(location.state.type, location.state.message);
      // Limpa o estado para que a mensagem não apareça novamente ao navegar
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, showNotification, navigate, location.pathname]);

  const actions = useMemo(
    () => [
      {
        id: "details",
        icon: faEye,
        title: "Ver Detalhes",
        buttonClass: "btn-info",
        permission: "Ver fornecedores",
        onClick: (supplier) =>
          navigate(`/aluguel/fornecedores/detalhes/${supplier.id}`),
        show: (supplier) =>
          !supplier.isDeleted && canAccess("Ver fornecedores"),
      },
    ],
    [canAccess, navigate],
  );

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Fornecedores"
        showBackButton={true}
        backUrl="/dashboard"
      />
      <div className="container-fluid p-1">
        <FilterCollapse
          autoCompleteFields={inputsfilters}
          onSubmit={handleSubmit}
          onClear={handleClear}
        />

        <TableWithInactiveFilter
          data={suppliers}
          columns={columns}
          actions={actions}
          totalPages={totalPages}
          currentPage={pagination.page - 1}
          onPageChange={changePage}
          rowsPerPage={pagination.perPage}
          onRowsPerPageChange={(event) => {
            const newPerPage = +event.target.value;
            setPagination({ page: 1, perPage: newPerPage });
          }}
          filterValues={localFilterValues}
          localFilterValues={localFilterValues}
          applyUrlFilters={applyUrlFilters}
          type="default"
          isLoading={isLoadingSuppliers}
          isFetching={isFetchingSuppliers}
        />

        <ConfirmationModal
          open={openModalConfirmation}
          onClose={handleCancelConfirmation}
          onConfirm={handleConfirmAction}
          itemName={selectedItem ? selectedItem.name : ""}
          text={action.text}
        />
      </div>
    </MainLayout>
  );
};

export default SuppliersPage;
